import React, { useEffect, useState } from "react";
import { Sparkles, Copy, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const MODELS = [
  { provider: "anthropic", name: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", desc: "Best for emotional, ceremonial writing" },
  { provider: "openai", name: "gpt-5.2", label: "GPT-5.2", desc: "Crisp, modern, articulate" },
  { provider: "gemini", name: "gemini-3-flash-preview", label: "Gemini 3 Flash", desc: "Fast, fresh, contemporary" },
];
const TONES = [["romantic", "Romantic"], ["heartfelt", "Heartfelt"], ["humorous", "Humorous"], ["modern", "Modern"], ["traditional", "Traditional"], ["poetic", "Poetic"]];

export default function VowStudioPage() {
  const [form, setForm] = useState({
    model_provider: "anthropic", model_name: "claude-sonnet-4-5-20250929",
    partner_name: "", partner_qualities: "", story_highlights: "",
    tone: "romantic", length: "medium", include_promise: true,
  });
  const [vow, setVow] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  async function loadHistory() {
    const { data } = await api.get("/ai/vows/history");
    setHistory(data);
  }
  useEffect(() => { loadHistory().catch(() => {}); }, []);

  async function generate(e) {
    e.preventDefault();
    setBusy(true);
    setVow("");
    try {
      const { data } = await api.post("/ai/vows", form);
      setVow(data.vow);
      toast.success(`Generated with ${data.model}`);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  function copyVow() {
    navigator.clipboard.writeText(vow);
    toast.success("Copied");
  }

  return (
    <div data-testid="vow-studio">
      <p className="font-script text-[var(--gold)] text-3xl mb-1">AI Vow Studio</p>
      <h1 className="font-serif font-light text-4xl mb-3">Write your vows. Beautifully.</h1>
      <p className="text-[var(--muted)] mb-8 max-w-xl">Tell us about your love. We'll write personalized vows in seconds — choose your tone, your model, and edit until they feel like you.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={generate} className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">AI Model</label>
            <div className="grid grid-cols-1 gap-2">
              {MODELS.map((m) => (
                <button
                  key={m.name}
                  type="button"
                  data-testid={`model-${m.provider}`}
                  onClick={() => setForm({ ...form, model_provider: m.provider, model_name: m.name })}
                  className={`text-left p-3 rounded-xl border transition-all ${form.model_provider === m.provider ? "border-[var(--gold)] bg-[var(--bg-warm)]" : "border-[var(--line)] hover:border-[var(--gold)]"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{m.label}</span>
                    {form.model_provider === m.provider && <Sparkles className="w-4 h-4 text-[var(--gold)]" />}
                  </div>
                  <p className="text-xs text-[var(--muted)] mt-1">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Your partner's name</label><input data-testid="vow-partner-name" required value={form.partner_name} onChange={(e) => setForm({...form, partner_name: e.target.value})} className="input-luxe" /></div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">What you love about them</label><textarea data-testid="vow-qualities" required rows={3} value={form.partner_qualities} onChange={(e) => setForm({...form, partner_qualities: e.target.value})} placeholder="Their laugh, their kindness, the way they show up…" className="input-luxe resize-none" /></div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Stories or memories to include (optional)</label><textarea rows={3} value={form.story_highlights} onChange={(e) => setForm({...form, story_highlights: e.target.value})} placeholder="The trip to Paris, the day we adopted Charlie…" className="input-luxe resize-none" /></div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Tone</label>
              <select value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})} className="input-luxe">{TONES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Length</label>
              <select value={form.length} onChange={(e) => setForm({...form, length: e.target.value})} className="input-luxe"><option value="short">Short (~1 min)</option><option value="medium">Medium (~2 min)</option><option value="long">Long (~3 min)</option></select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--muted)]">
            <input type="checkbox" checked={form.include_promise} onChange={(e) => setForm({...form, include_promise: e.target.checked})} />
            Include specific promises
          </label>

          <button type="submit" disabled={busy} data-testid="vow-generate" className="btn-gold w-full"><Wand2 className="w-4 h-4" /> {busy ? "Writing…" : "Generate My Vows"}</button>
        </form>

        <div className="bg-[var(--bg-warm)] rounded-2xl p-6 md:p-8 min-h-[400px] relative">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Your Vows</p>
            {vow && <button onClick={copyVow} className="text-xs text-[var(--gold)] flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>}
          </div>
          {busy && (
            <div className="flex items-center justify-center h-72"><div className="text-center"><div className="w-10 h-10 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-script text-2xl text-[var(--gold)]">Writing from the heart…</p></div></div>
          )}
          {!busy && vow && (
            <div className="font-serif text-lg leading-relaxed text-[var(--ink)] whitespace-pre-line" data-testid="vow-output">{vow}</div>
          )}
          {!busy && !vow && (
            <div className="text-center pt-12 text-[var(--muted)]">
              <Sparkles className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
              <p className="font-serif text-xl mb-2">Your vows will appear here</p>
              <p className="text-sm">Fill in the form, choose a model, and we'll do the rest.</p>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-8 bg-white border border-[var(--line)] rounded-2xl p-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4">Previous drafts</p>
          <div className="space-y-3">
            {history.slice(0, 5).map((h) => (
              <button key={h.draft_id} onClick={() => setVow(h.vow_text)} className="block w-full text-left py-3 border-t border-[var(--line)] hover:bg-[var(--bg-warm)] -mx-3 px-3 rounded">
                <p className="text-sm font-medium">For {h.partner_name} <span className="text-[var(--muted)]">· {h.tone} · {h.length} · {h.model_provider}</span></p>
                <p className="text-xs text-[var(--muted)] line-clamp-1 mt-1">{h.vow_text.slice(0, 100)}…</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
